import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  getCityAutocomplete,
  isExactPakistanCity,
  searchPakistanCities,
} from '../lib/pakistanCities';
import { capitalizeWordsLive } from '../lib/textFormat';
import { contextFieldInputClass } from './ui/ContextTypography';

export interface CityAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  error?: string;
  placeholder?: string;
  province?: string;
  variant?: 'onboarding' | 'settings' | 'context' | 'case-modal';
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  onMouseDown?: (e: React.MouseEvent<HTMLInputElement>) => void;
}

export default function CityAutocompleteInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  error,
  placeholder,
  province,
  variant = 'onboarding',
  autoFocus = false,
  disabled = false,
  className = '',
  inputClassName = '',
  onMouseDown,
}: CityAutocompleteInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const userEditedRef = useRef(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(
    () => Boolean(value?.trim()),
  );
  const listId = useId();

  useEffect(() => {
    if (!userEditedRef.current) {
      setSuggestionsDismissed(Boolean(value.trim()));
    }
  }, [value]);

  const exactMatch = useMemo(
    () => isExactPakistanCity(value, province),
    [value, province],
  );

  const completion = useMemo(
    () => (exactMatch ? null : getCityAutocomplete(value, province)),
    [value, province, exactMatch],
  );
  const suggestions = useMemo(() => {
    if (suggestionsDismissed || exactMatch || !value.trim()) return [];
    return searchPakistanCities(value, province, 6);
  }, [value, province, suggestionsDismissed, exactMatch]);

  const selectCity = (city: string) => {
    onChange(city);
    setHighlightedIndex(0);
    setSuggestionsDismissed(true);
    userEditedRef.current = false;
    inputRef.current?.focus();
  };

  const acceptCompletion = () => {
    if (!completion) return;
    onChange(completion.full);
    setHighlightedIndex(0);
    setSuggestionsDismissed(true);
    userEditedRef.current = false;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      setSuggestionsDismissed(false);
    } else if (suggestionsDismissed) {
      onKeyDown?.(e);
      return;
    }

    if (completion && (e.key === 'ArrowRight' || e.key === 'Tab')) {
      const input = inputRef.current;
      const atEnd =
        !input ||
        (input.selectionStart === input.selectionEnd &&
          input.selectionEnd === value.length);

      if (atEnd) {
        e.preventDefault();
        acceptCompletion();
        return;
      }
    }

    if (e.key === 'ArrowDown' && suggestions.length > 0) {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, suggestions.length - 1));
      return;
    }

    if (e.key === 'ArrowUp' && suggestions.length > 0) {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
      return;
    }

    if (e.key === 'Enter' && suggestions.length > 0 && suggestions[highlightedIndex]) {
      const selected = suggestions[highlightedIndex];
      if (selected.toLowerCase().startsWith(value.trim().toLowerCase())) {
        e.preventDefault();
        selectCity(selected);
        return;
      }
    }

    onKeyDown?.(e);
  };

  const isOnboarding = variant === 'onboarding';
  const isCaseModal = variant === 'case-modal';
  const isContext = variant === 'context';

  const defaultInputClass = isOnboarding
    ? `w-full text-2xl md:text-4xl bg-transparent border-b-2 py-4 outline-none placeholder:text-white/20 font-medium transition-colors ${
        error ? 'border-red-400/80 focus:border-red-400' : 'border-white/20 focus:border-white'
      }`
    : isCaseModal
      ? `w-full p-2.5 bg-bg-secondary border border-border-light rounded-xl focus:bg-white focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/20 outline-none transition-all text-sm text-center placeholder:text-center text-text-main caret-text-main ${
          error ? 'border-red-400' : ''
        }`
      : isContext
        ? `${contextFieldInputClass} ${error ? 'border-red-400' : ''}`
        : `w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-primary outline-none ${
            error ? 'border-red-400' : 'border-border-light'
          }`;

  const inputClass = `${defaultInputClass} ${inputClassName}`.trim();

  const ghostClass = isOnboarding
    ? 'text-2xl md:text-4xl font-medium text-white/25 pointer-events-none select-none whitespace-pre'
    : isCaseModal
      ? 'text-sm text-center text-slate-400 pointer-events-none select-none whitespace-pre'
      : 'text-base text-slate-400 pointer-events-none select-none whitespace-pre';

  const listClass = isOnboarding
    ? 'mt-2 max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-[#1E293B]/95 shadow-lg custom-scrollbar'
    : 'mt-1 max-h-48 overflow-y-auto rounded-xl border border-border-light bg-white shadow-lg z-30 relative';

  const itemClass = (active: boolean) =>
    isOnboarding
      ? `w-full px-4 py-2.5 text-left text-sm transition-colors ${
          active ? 'bg-blue-500/20 text-white' : 'text-slate-300 hover:bg-white/5'
        }`
      : isCaseModal
        ? `w-full px-4 py-2 text-center text-sm transition-colors ${
            active ? 'bg-primary/10 text-primary' : 'text-[#0F172A] hover:bg-slate-50'
          }`
        : `w-full px-4 py-2 text-left text-sm transition-colors ${
            active ? 'bg-primary/10 text-primary' : 'text-[#0F172A] hover:bg-slate-50'
          }`;

  const hintClass = isOnboarding ? 'text-slate-500 text-xs mt-2' : 'text-slate-400 text-xs mt-1';
  const errorClass = isOnboarding ? 'text-red-400 text-sm mt-2' : 'text-red-600 text-sm mt-1';

  const showList = suggestions.length > 0;
  const showGhostCompletion = Boolean(completion?.suffix);
  const ghostPadding = isOnboarding
    ? 'py-4'
    : isCaseModal
      ? 'px-2.5 py-2.5'
      : isContext
        ? 'px-3 py-2.5'
        : 'px-4 py-2';

  return (
    <div className={`w-full ${className}`.trim()}>
      <div className="relative">
        <div
          className={`absolute inset-0 z-0 flex items-center overflow-hidden pointer-events-none ${
            isCaseModal ? 'justify-center' : ''
          } ${ghostPadding}`}
          aria-hidden
        >
          <span className={`${ghostClass} invisible`}>{value}</span>
          {showGhostCompletion ? (
            <span className={ghostClass}>{completion!.suffix}</span>
          ) : null}
        </div>
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          autoFocus={autoFocus}
          data-onboarding-focus={isOnboarding ? true : undefined}
          disabled={disabled}
          className={`${inputClass} relative z-[1] w-full ${
            isOnboarding || isCaseModal ? 'bg-transparent' : ''
          } pointer-events-auto disabled:opacity-60 disabled:cursor-not-allowed`}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            const next = capitalizeWordsLive(e.target.value);
            userEditedRef.current = true;
            onChange(next);
            setHighlightedIndex(0);
            setSuggestionsDismissed(false);
          }}
          onMouseDown={onMouseDown}
          onFocus={() => {
            if (exactMatch) return;
            userEditedRef.current = true;
            setSuggestionsDismissed(false);
          }}
          onBlur={(e) => onBlur?.(e.target.value)}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={listId}
        />
      </div>

      {showGhostCompletion && (
        <p className={`${hintClass} ${isCaseModal ? 'text-center' : ''}`}>
          Press → or Tab to complete
        </p>
      )}

      {showList && (
        <ul id={listId} role="listbox" className={listClass}>
          {suggestions.map((city, idx) => (
            <li key={city} role="option" aria-selected={idx === highlightedIndex}>
              <button
                type="button"
                className={itemClass(idx === highlightedIndex)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectCity(city)}
              >
                {city}
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className={errorClass}>{error}</p>}
    </div>
  );
}
